import { useCallback, useEffect, useState, type SyntheticEvent } from 'react';
import { api, ApiRequestError } from '../lib/api';
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Screen,
  inputClass,
} from '../components/ui';
import type { Goal } from '../../shared/types';

export function Why() {
  const [goals, setGoals] = useState<Goal[] | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setGoals(await api.goals());
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load your goals.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function add(event: SyntheticEvent) {
    event.preventDefault();
    if (draft.trim().length === 0) return;
    setBusy(true);
    try {
      await api.createGoal({ text: draft.trim() });
      setDraft('');
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not add that.');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(goal: Goal) {
    await api.updateGoal(goal.id, { active: !goal.active });
    await load();
  }

  async function remove(goal: Goal) {
    await api.deleteGoal(goal.id);
    await load();
  }

  if (!goals) {
    return (
      <Screen title="Why">
        {error ? <ErrorNote>{error}</ErrorNote> : <Loading label="Loading your why" />}
      </Screen>
    );
  }

  return (
    <Screen title="Why">
      <p className="-mt-2 text-sm text-mist-400">
        Vision gives the reps meaning. Reps create evidence. Evidence changes self-conception.
      </p>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {goals.length === 0 ? (
        <Empty title="No goals recorded." />
      ) : (
        <ul className="flex flex-col gap-2">
          {goals.map((goal) => (
            <li key={goal.id}>
              <Card className="flex items-start justify-between gap-3">
                <div className={goal.active ? '' : 'opacity-50'}>
                  <p className="text-base text-mist-200">{goal.text}</p>
                  <p className="mt-1 text-xs tracking-[0.14em] text-mist-400 uppercase">
                    {goal.category} · importance {goal.importance}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-1">
                  <Button
                    variant="ghost"
                    className="px-2 text-xs"
                    onClick={() => void toggle(goal)}
                  >
                    {goal.active ? 'Pause' : 'Resume'}
                  </Button>
                  <Button
                    variant="ghost"
                    className="px-2 text-xs"
                    onClick={() => void remove(goal)}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Card>
        <form onSubmit={(event) => void add(event)} className="flex flex-col gap-3">
          <Field label="Add a goal">
            <input
              className={inputClass}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              placeholder="What becomes possible?"
            />
          </Field>
          <Button type="submit" variant="secondary" disabled={busy || draft.trim().length === 0}>
            Add
          </Button>
        </form>
      </Card>
    </Screen>
  );
}
